// @flow

import {Language, Paging, CompaniesSortBy, TabDataFlags} from '@/types'

import debounce from 'lodash.debounce'
import React, {Component} from 'react'
import {bindActionCreators} from 'redux'
import {connect} from 'react-redux'
import Pagination from '@/controls/pagination'
import {appSelectors, portfoliosActions, portfoliosSelectors, uiActions} from '@/store'

import AddPortfolioButton from './add-portfolio-button'
import EditPortfolioButton from './edit-portfolio-button'
import CountrySelect from '@/controls/country-select'
import CompaniesTable from './companies-table'
import DeletePortfolioButton from './delete-portfolio-button'
import Section from '@/elements/section'
import SearchCompanies from './search-companies'
import UploadFileButton from './upload-file-button'
import MoveButton from './move-button'
import CopyButton from './copy-button'
import DeleteButton from './delete-button'
import {isAnyTabDataLoaded, isPermissionEnabled} from '@/views/util'
import SyncButton from './refresh-button'
import ShareButton from './share-portfolio-button'

import './index.css'

type Props = {
  companiesCount: number,
  countries: Array<string>,
  portfolioIsDefault: boolean,
  countryFilter: string,
  importCompaniesFile: (type: string) => void,
  syncCompaniesFile: (type: string) => void,
  setPortfolioCountryFilter: (string) => void,
  fetchPortfolioCompanies: () => void,
  setPortfolioPageNumber: (number) => void,
  setPortfolioCompaniesSortOptions: (sortBy: CompaniesSortBy) => void,
  resetPortfolioCompaniesSortOptions: () => void,
  portfolioPaging: Paging,
  l: Language,
  tabDataFlags: TabDataFlags,
  fetchPortfoliosTabInitialData: () => void,
  fetchCommonTabsData(): () => void,
  isLoading: boolean,
  selectedCompanies: ?Object,
  isActivePortfolioShared: boolean,
  activePortfolioPermissions: string,
  activePortfolioId: number,
}

// const getCompaniesTotalCountLabel = (label, count) => {
//   return count > 0 ? `${label} - ${count}` : label
// }

const pageChange = (props) => (pageNumber) => {
  props.setPortfolioPageNumber(pageNumber - 1)
  props.fetchPortfolioCompanies()
}

const sortBy = (props) => (sortBy: CompaniesSortBy) => {
  props.setPortfolioPageNumber(0)
  props.setPortfolioCompaniesSortOptions(sortBy)
  props.fetchPortfolioCompanies()
}

const getCompaniesByCountry = (props) => (country) => {
  props.setPortfolioCountryFilter(country)
  props.setPortfolioPageNumber(0)
  props.resetPortfolioCompaniesSortOptions()
  props.fetchPortfolioCompanies()
}

const checkAndFetchData = (props) => {
  // call apis to fetch this tab data
  if (!props.tabDataFlags.isPortfoliosDataLoaded && !props.isLoading) {
    // fetch common tab data if no tab is loaded yet
    if (!isAnyTabDataLoaded(props.tabDataFlags)) {
      props.fetchCommonTabsData()
    }
    // fetch this tab data
    props.fetchPortfoliosTabInitialData()
  }
}

const checkIsAllPortfolio = ({activePortfolioId}) => activePortfolioId !== 0

const getCompaniesCount = (selectedCompanies) => {
  if (selectedCompanies) {
    return selectedCompanies.isAll
      ? selectedCompanies.totalCompanies
      : (selectedCompanies.data || []).length
  }
  return 0
}

const renderSelectedCompanies = ({selectedCompanies, l, companiesCount, activePortfolioId}) => {
  return (activePortfolioId === 0) ? `${l('Total Companies')} - ${companiesCount}`
    : `${l('Selected Companies')} - ${getCompaniesCount(selectedCompanies)} ${l('of')} ${selectedCompanies ? (selectedCompanies.totalCompanies || 0) : 0}`
}

const renderEditPortfolioButton = ({isActivePortfolioShared, activePortfolioPermissions}) => {
  return (!isActivePortfolioShared || isPermissionEnabled(activePortfolioPermissions, 'Edit'))
    ? <EditPortfolioButton/> : null
}

const renderDeletePortfolioButton = ({portfolioIsDefault, isActivePortfolioShared, activePortfolioPermissions}) =>
  !(portfolioIsDefault || isActivePortfolioShared)
    ? <DeletePortfolioButton /> : null

const renderImportButton = ({importCompaniesFile, isActivePortfolioShared, activePortfolioPermissions}) =>
  (!isActivePortfolioShared || isPermissionEnabled(activePortfolioPermissions, 'Add'))
    ? <UploadFileButton
      id={'import_from_file'}
      title={'Import from file'}
      onConfirm={(type = 'file') => importCompaniesFile(type)}
      type='import'
    /> : null

const renderSyncButton = ({syncCompaniesFile, isActivePortfolioShared, activePortfolioPermissions}) =>
  (!isActivePortfolioShared || (isPermissionEnabled(activePortfolioPermissions, 'Add') &&
    isPermissionEnabled(activePortfolioPermissions, 'Remove')))
    ? <UploadFileButton
      id={'sync_from_file'}
      title={'Sync from file'}
      onConfirm={(type = 'file') => syncCompaniesFile(type)}
      type='sync'
    /> : null

const renderShareButton = ({isActivePortfolioShared}) =>
  !isActivePortfolioShared ? <ShareButton/> : null

const renderMoveButton = ({isActivePortfolioShared, activePortfolioPermissions}, isDisabled) =>
  (!isActivePortfolioShared)
    ? <MoveButton disabled={isDisabled}/>
    : null

const renderCopyButton = ({isActivePortfolioShared}, isDisabled) =>
  (!isActivePortfolioShared)
    ? <CopyButton disabled={isDisabled}/>
    : null

const renderDeleteButton = ({isActivePortfolioShared, activePortfolioPermissions}, isDisabled) =>
  (!isActivePortfolioShared || isPermissionEnabled(activePortfolioPermissions, 'Remove'))
    ? <DeleteButton disabled={isDisabled}/>
    : null

export class RawPortfoliosPage extends Component<Props, *> {
  constructor (props: Props) {
    super(props)
    const windowDimensions = {
      windowLength: window.outerHeight,
    }
    this.state = windowDimensions
  }
  updateDimensions = () => {
    this.setState({
      windowLength: window.outerHeight,
    })
  }
  render = () => {
    const {props} = this
    const isDisabled = props.selectedCompanies
      ? !props.selectedCompanies.isAll && props.selectedCompanies.data.length === 0
      : false
    return (
      <div style={{maxHeight: (this.state.windowLength ? this.state.windowLength - 428 : 300)}}>
        <Section header={renderSelectedCompanies(props)} className="portfolio">
          <SyncButton/>
          <div className="row">
            <div className="col-xs-12">
              <ul className="list-inline pull-left">
                <li>
                  <AddPortfolioButton />
                  {checkIsAllPortfolio(this.props)
                    ? <span>
                      { renderEditPortfolioButton(props) }
                      { renderDeletePortfolioButton(props) }
                    </span> : null
                  }
                </li>
              </ul>
              <ul className="form-inline pull-right filters">
                {
                  checkIsAllPortfolio(this.props)
                    ? <span>
                      {renderImportButton(props)}
                      {renderSyncButton(props)}
                      {renderShareButton(props)}
                    </span> : null
                }
                <CountrySelect
                  allOption={true}
                  availableCountries={props.countries}
                  selected={props.countryFilter}
                  onChange={getCompaniesByCountry(props)}
                />
                <SearchCompanies />
              </ul>
            </div>
            <div className="row">
              <div className="col-xs-12">
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 overflow-table">
              <CompaniesTable sortBy={sortBy(props)} pageNo={props.portfolioPaging.pageNumber}
                selectedCompanies={props.selectedCompanies}/>
              {
                props.companiesCount > props.portfolioPaging.pageSize ? <Pagination
                  activePage={props.portfolioPaging.pageNumber + 1}
                  pageSize={props.portfolioPaging.pageSize}
                  totalItems={props.companiesCount}
                  onChange={pageChange(props)}
                /> : false
              }
            </div>
          </div>
          {(props.selectedCompanies && (props.selectedCompanies.isAll || props.selectedCompanies.data.length > 0)) || props.companiesCount > 0
            ? <div className={props.companiesCount > props.portfolioPaging.pageSize ? 'row' : 'row move-copy-delete'}>
              <div className='col-md-12'>
                <ul className="list-inline text-center">
                  <li>
                    {
                      checkIsAllPortfolio(this.props)
                        ? <span>
                          {renderMoveButton(props, isDisabled)}
                          {renderCopyButton(props, isDisabled)}
                          {renderDeleteButton(props, isDisabled)}
                        </span> : null
                    }
                  </li>
                </ul>
              </div>
            </div> : null }
        </Section>
      </div>
    )
  }

  componentDidMount = () => {
    checkAndFetchData(this.props)
    window.addEventListener('resize', this.updateDimensions)
  }

  componentWillReceiveProps = (nextProps: Props) => {
    checkAndFetchData(nextProps)
  }
}

const mapStateToProps = (state) => ({
  companiesCount: portfoliosSelectors.getActivePortfolioCompaniesCount(state),
  countries: portfoliosSelectors.getActivePortfolioCountries(state),
  portfolioIsDefault: portfoliosSelectors.isActivePortfolioDefault(state),
  countryFilter: state.ui.portfolioCountryFilter,
  portfolioPaging: state.ui.portfolioPaging,
  l: appSelectors.getL(state),
  tabDataFlags: state.app.tabDataFlags,
  isLoading: state.app.showAppLoader,
  selectedCompanies: portfoliosSelectors.getSelectedCompanies(state),
  isActivePortfolioShared: portfoliosSelectors.isActivePortfolioShared(state),
  activePortfolioPermissions: portfoliosSelectors.getActivePortfolioPermissions(state),
  activePortfolioId: state.app.activePortfolioId,
})

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(portfoliosActions, dispatch),
  ...bindActionCreators(uiActions, dispatch),
  fetchPortfolioCompanies: debounce(
    () => dispatch(portfoliosActions.fetchPortfolioCompanies()),
    200,
  ),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(RawPortfoliosPage)



// WEBPACK FOOTER //
// ./src/views/portfolios-page/index.js